import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Building, Users, MapPin, MoreHorizontal, Edit, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { getPartners as apiGetPartners } from "@/services/api/partners";

const Partners = () => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{ totalPartners: number; activePartners: number; inactivePartners: number; totalCustomers: number }>({ totalPartners: 0, activePartners: 0, inactivePartners: 0, totalCustomers: 0 });
  const [partners, setPartners] = useState<Array<{
    id: string;
    name: string;
    status: string;
    type?: string;
    api_endpoint?: string;
    created_at?: string;
    customerCount?: number;
  }>>([]);
  const [filterCountry, setFilterCountry] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchPartners = useCallback(async (opts?: { force?: boolean }) => {
    const force = opts?.force === true;
    setLoading(true);
    setError(null);
    try {
      const filter = {
        partnerFullName: searchTerm?.trim() || undefined,
        country: filterCountry?.trim() || undefined,
        status: filterStatus === 'all' ? undefined : filterStatus,
      } as any;
      if (import.meta.env.DEV) {
        try {
          const raw = sessionStorage.getItem('workspaceData');
          const parsed = raw ? JSON.parse(raw) : null;
          console.debug('[Partners] Fetching with filters', { filter, sessionUserId: parsed?.userId, role: parsed?.role, force });
        } catch {}
      }
      const resp = await apiGetPartners(filter);
      const payload = resp?.data || resp?.Data || resp; // accept various shapes
      const totals = {
        totalPartners: Number(payload?.totalPartners ?? 0),
        activePartners: Number(payload?.activePartners ?? 0),
        inactivePartners: Number(payload?.inactivePartners ?? 0),
        totalCustomers: Number(payload?.totalCustomers ?? 0),
      };
      setStats(totals);

      const list: any[] = payload?.getPartners || [];
      const mapped = list.map((p) => ({
        id: p.partnerUniqueId || p.UniqueId || (typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : String(Math.random())),
        name: p.partnerFullName || p.partnerName || p.name || '—',
        status: String(p.status || 'Inactive').toLowerCase(),
        type: p.country || 'partner',
        api_endpoint: undefined,
        created_at: undefined,
        customerCount: p.customerCount ?? undefined,
      }));
      setPartners(mapped);
    } catch (e: any) {
      console.error('Failed to load partners', e);
      const resp = e?.response?.data;
      let detailed = '';
      if (resp?.errors && typeof resp.errors === 'object') {
        const firstKey = Object.keys(resp.errors)[0];
        const firstMsg = Array.isArray(resp.errors[firstKey]) ? resp.errors[firstKey][0] : String(resp.errors[firstKey]);
        detailed = firstMsg || '';
      }
      setError(detailed || resp?.message || e?.message || 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterCountry, filterStatus]);

  useEffect(() => {
    const nameLen = (searchTerm ?? '').trim().length;
    const countryLen = (filterCountry ?? '').trim().length;
    const meetsMin = ((nameLen === 0 || nameLen >= 3) && (countryLen === 0 || countryLen >= 3));
    if (import.meta.env.DEV) {
      console.debug('[Partners] effect deps changed', { searchTerm, filterCountry, filterStatus, meetsMin });
    }
    const handle = setTimeout(() => {
      if (meetsMin) {
        fetchPartners();
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [searchTerm, filterCountry, filterStatus, fetchPartners]);

  const onKeyDownInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const val = (e.currentTarget.value ?? '');
    if (e.key === 'Enter') {
      if (val.trim().length === 0 || val.trim().length >= 3) {
        e.preventDefault();
        e.stopPropagation();
        fetchPartners({ force: true });
      } else {
        e.preventDefault();
        e.stopPropagation();
      }
    }
    if (e.key === 'Backspace' && val.length === 0) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const filteredPartners = useMemo(() => partners.filter(partner =>
    partner.name.toLowerCase().includes(searchTerm.toLowerCase())
  ), [partners, searchTerm]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-red-600">{error}</CardContent>
        </Card>
      </div>
    );
  }


  return (
      <div className="space-y-8">
        {/* Page Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Partners</h1>
            <p className="text-muted-foreground mt-2">
              Manage your utility bill processing partners and their customer portfolios.
            </p>
          </div>
          {/* Add Partner temporarily disabled for backend-driven list */}
          <Button variant="outline" disabled title="Add Partner coming soon">Add Partner</Button>
        </div>

        {/* Search and Filters (server-side) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="mb-1 block">Partner Name</Label>
            <Input
              placeholder="Partner short name"
              className="placeholder:text-muted-foreground"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={onKeyDownInput}
            />
          </div>
          <div>
            <Label className="mb-1 block">Country</Label>
            <Input
              placeholder="Country"
              className="placeholder:text-muted-foreground"
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
              onKeyDown={onKeyDownInput}
            />
          </div>
          <div>
            <Label className="mb-1 block">Status</Label>
            <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v)}>
              <SelectTrigger>
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Partners Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPartners.map((partner) => {
            return (
              <Card key={partner.id} className="hover-lift shadow-card">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                     <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {partner.name.split(' ').map(word => word[0]).join('').slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                    <div>
                      <CardTitle className="text-lg">{partner.name}</CardTitle>
                      <Badge 
                        variant={partner.status === "active" ? "default" : "secondary"}
                        className="mt-1"
                      >
                        {partner.status}
                      </Badge>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem disabled>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" disabled>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="text-center p-2 bg-muted/30 rounded-lg">
                    <Users className="h-4 w-4 mx-auto mb-1 text-primary" />
                    <p className="text-sm font-medium">{partner.customerCount ?? 0}</p>
                    <p className="text-xs text-muted-foreground">Customers</p>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  {partner.api_endpoint && (
                    <div className="flex items-center space-x-2">
                      <Building className="h-3 w-3 text-muted-foreground" />
                      <span className="text-muted-foreground text-xs">{partner.api_endpoint}</span>
                    </div>
                  )}
                </div>
                
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground">
                    {partner.created_at ? `Created ${new Date(partner.created_at).toLocaleDateString()}` : ''}
                  </p>
                </div>
              </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Partners</p>
                  <p className="text-2xl font-bold">{stats.totalPartners || partners.length}</p>
                </div>
                <Building className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Customers</p>
                  <p className="text-2xl font-bold">{stats.totalCustomers}</p>
                </div>
                <Users className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Partners</p>
                  <p className="text-2xl font-bold">{stats.activePartners || partners.filter(p => p.status === 'active').length}</p>
                </div>
                <MapPin className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
  );
};

export default Partners;
